// Turns one registered material into its physical and monetary effect.
//
// THREE SCALING RULES, taken from the interventie's eenheid (see `scope` in
// adaptInterventieDocument):
//
//   perUnit     units = quantity x unitsPerItem
//   perResident units = the household's resident count  — quantity ignored
//   perDwelling units = 1                               — quantity ignored
//
// Ignoring quantity looks wrong until you read what the kengetal prices. ET07
// is priced per bewoner: residents shower a fixed amount, so a second shower
// head does not double the hot water saved. ET10 is priced per woning: a second
// radiator fan is not a second dwelling. Multiplying by quantity there would
// inflate exactly the materials an energiebox hands out most.
//
// NEVER THROWS, and never substitutes 0 for unknown — same contract as
// resolveMaterialIntervention, for the same autosave reason.
// AI-CONTEXT: docs/pitfalls.md#ui-strict-validator-kills-autosave
//
// AI-CONTEXT: See docs/features/materials-co2-circulariteit.md

/**
 * @typedef {import('./resolveMaterialIntervention').ResolvedMaterialIntervention} ResolvedMaterialIntervention
 *
 * @typedef {object} MaterialImpact
 * @property {'ok'|'unmapped'|'unknown-code'|'needs-factor'|'no-kengetal'|'needs-residents'|'needs-quantity'} status
 * @property {string|null} code
 * @property {'perYear'|'perUnit'|null} basis
 * @property {number|null} units - Registration units this row contributes
 * @property {number|null} co2Kg
 * @property {number|null} privateEur - Household bill saving; null for one-off interventies
 * @property {number|null} societalEur
 */

/**
 * @param {'unmapped'|'unknown-code'|'needs-factor'|'no-kengetal'|'needs-residents'|'needs-quantity'} status
 * @param {string|null} code
 * @returns {MaterialImpact}
 */
const noFigure = (status, code = null) => ({
    status,
    code,
    basis: null,
    units: null,
    co2Kg: null,
    privateEur: null,
    societalEur: null,
})

/**
 * @param {*} value
 * @returns {number|null} A positive finite number, or null
 */
const positiveNumberOr = (value) => {
    if (value === '' || value === null || value === undefined) return null
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/**
 * @param {{quantity: *, resolved: ResolvedMaterialIntervention}} row
 * @param {{residents?: *}} context - The household's resident count, when known
 * @returns {MaterialImpact}
 */
const computeMaterialImpact = (row, context) => {
    const resolved = row?.resolved
    if (!resolved || resolved.status === 'unmapped') return noFigure('unmapped')
    if (resolved.status !== 'ok') return noFigure(resolved.status, resolved.code)

    const intervention = resolved.intervention
    const { scope } = intervention

    let units = null
    if (scope === 'perResident') {
        units = positiveNumberOr(context?.residents)
        if (units === null) return noFigure('needs-residents', resolved.code)
    } else if (scope === 'perDwelling') {
        units = 1
    } else {
        const quantity = positiveNumberOr(row?.quantity)
        if (quantity === null) return noFigure('needs-quantity', resolved.code)
        units = quantity * resolved.unitsPerItem
    }

    return {
        status: 'ok',
        code: resolved.code,
        basis: intervention.basis,
        units,
        co2Kg: intervention.co2KgPerUnit * units,
        // Null stays null: a one-off interventie lowers nobody's energy bill,
        // and 0 would read as "we checked, it saves nothing".
        privateEur: intervention.privateEurPerUnit === null
            ? null
            : intervention.privateEurPerUnit * units,
        societalEur: intervention.societalEurPerUnit * units,
    }
}

export default computeMaterialImpact
